// Saved fertility-rec SETS: "save those rec layers so you can reference them later.
import { api } from "../api";
import { qk } from "../hooks/queries";
import { queryClient } from "./queryClient";
import { useRecSetsBus } from "../stores/recSetsBus";
import type { RecInputs } from "./fertility";
import type { Replacement } from "./replacement";

// Notify subscribers (dashboard Action-Flag filter + pills) that the saved rec sets changed, so
// their `loadRecSets`-derived memos recompute instead of caching a stale localStorage snapshot.
const bumpRecSets = () => useRecSetsBus.getState().bump();

export interface SavedRecSet {
  id: string;
  name: string;
  createdAt: string; // ISO
  fieldId: string;
  /** Boundary version this Rx was built on (`/grid`.boundary_version_id). */
  gridId?: string;
  /** New saves are `"grid"`. Legacy documents omit this until the server upgrades them. */
  cellOrder?: "grid";
  cellCount: number; // centroid count at compute time (alignment guard)
  /**
   * Legacy documents only. New saves omit it. The server copies it to
   * `legacyStackVersion` when it remaps the document onto the field grid.
   * Readers do not compare it to a stack content version.
   */
  stackVersion?: string | number;
  legacyStackVersion?: string | number;
  /** Server tag. A reload or spread shows `rebuildMessage` and does not remap. */
  gridStatus?: "rebuild";
  rebuildMessage?: string;
  nutrients: string[]; // which base recs: subset of ['lime','p2o5','k2o']
  // the LAST YEAR this rec is considered current for, for the "Needs {year} <nutrient> Rx" action
  // flags created-year + coverage − 1, where coverage = crop-removal season count for P/K
  // (build-only = 1), and Lime is always 4 years.
  coversThroughYear?: number;
  cation: boolean; // BCSR cation-balance correction included
  cationCaMg?: number; // preferred Ca:Mg target (meq) when cation balance is on (default 6.5)
  cationMgK?: number; // preferred Mg:K target (meq) when cation balance is on (default 2.0)
  sources: Record<string, string>; // property -> the ml-* layer id used
  soil: RecInputs; // per-cell sampled soil-test values (rec layers recompute from these)
  replacement?: Replacement | null; // crop-removal replacement (static or yield-derived), if added
  replP?: boolean;
  replK?: boolean; // apply the crop-removal to K₂O
  kfix?: boolean; // K build-up adjusted for clay K-fixation (the per-cell efficiency rides `soil.kFixEff`)
  kfixSet?: "conservative" | "standard"; // which coefficient set drove the K-fixation adjustment
  kfixRelease?: number; // in-season K release discount used (0..1)
  pMin?: boolean; // natural P mineralization credit subtracted from P₂O₅ (the per-cell credit rides `soil.pMinCredit`)
  albrechtK?: boolean; // K₂O base ran off cation-saturation targeting instead of the critical level
  albrechtLime?: boolean; // Lime base ran off the Ca-saturation target (65% default) instead of ΔpH-to-6.5
  albrechtTargets?: import("./fertility").AlbrechtTargets; // the editable saturation %s used
  pCritCustom?: number;
  kCritCustom?: number;
  phTargetCustom?: number;
  // Absent: window the base, then add corrections (sets saved before this pin).
  // "final": min/max/drop apply to the rate after Genius adjustments.
  rateWindow?: "final";
  /**
   * Server-owned snapshot of the cells, applied rates, and categorical legend
   * at save. Readers display this block. They do not recompute it.
   */
  frozen?: {
    v: number;
    at?: string;
    cells?: {
      kind?: "grid" | "snapshot";
      gridId?: string;
      coreVersion?: string;
      cellCount?: number;
      uri?: string;
      sha256?: string;
      order?: string;
    } | null;
    reason?: string;
    rates?: {
      p2o5?: (number | null)[];
      k2o?: (number | null)[];
      lime?: (number | null)[];
    };
    engine?: string;
    basis?: string;
    legend?: Record<
      string,
      { value?: unknown; code: number; label: string; color: string }[]
    >;
  };
}

const KEY = (fieldId: string) => `sm_fert_sets:${fieldId}`;
const MAX_SETS = 12; // the localStorage MIRROR's budget (~5 MB origin quota); memory is uncapped
const COVER_PREFIX = "sm_fert_cover:";

/* * the last year a saved rec is current for (falls back to its created year for older sets w/o the field) */
export function coversThrough(s: {
  coversThroughYear?: number;
  createdAt?: string;
}): number {
  if (typeof s.coversThroughYear === "number") return s.coversThroughYear;
  const y = new Date(s.createdAt ?? "").getFullYear();
  return Number.isFinite(y) ? y : 0;
}

/*
 * A saved K Rx reached the server and the Spread panel still said there was
 * none, because every synchronous reader went through the localStorage mirror.
 * That mirror is capped at MAX_SETS and sheds under quota (a set carries the
 * full per-cell soil arrays). This map is the session answer: save, delete,
 * and hydrate keep it uncapped, `loadRecSets` serves it first, and
 * localStorage is only a warm-start cache.
 */
const memSets = new Map<string, SavedRecSet[]>();

/* * Test hook: module-level memory outlives a test's localStorage.clear. */
export function resetRecSetsMemoryForTests(): void {
  memSets.clear();
}

export function purgeFieldRecSets(fieldId: string): void {
  memSets.delete(fieldId);
  try {
    localStorage.removeItem(KEY(fieldId));
    localStorage.removeItem(`${COVER_PREFIX}${fieldId}`);
  } catch {
    /* private mode */
  }
  bumpRecSets();
}

export function loadRecSets(fieldId: string): SavedRecSet[] {
  const mem = memSets.get(fieldId);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(KEY(fieldId));
    const arr = raw ? (JSON.parse(raw) as SavedRecSet[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/*
 *
 * That is why hydrate's RETURN VALUE is the truth and a re-read is not: consumers must use it.
 */
function persistRecSets(fieldId: string, list: SavedRecSet[]): SavedRecSet[] {
  let out = list.slice(0, MAX_SETS);
  for (;;) {
    try {
      localStorage.setItem(KEY(fieldId), JSON.stringify(out));
      return out;
    } catch {
      if (out.length <= 1) return loadRecSets(fieldId); // even one won't fit — report reality
      out = out.slice(0, -1); // drop the oldest and retry
    }
  }
}

// Save a set (newest first), pruning oldest past the cap and shedding sets if the write
// hits the quota. Returns the list that ACTUALLY persisted — the retry prunes all the way
// down to a single set before giving up, and on total failure re-reads storage so the
// caller never shows an in-memory list that was never written (silent save loss).
export function saveRecSet(set: SavedRecSet): SavedRecSet[] {
  const list = [
    set,
    ...loadRecSets(set.fieldId).filter((s) => s.id !== set.id),
  ];
  memSets.set(set.fieldId, list); // the session answer — a quota-shed mirror can no longer lose the save.
  persistRecSets(set.fieldId, list); // warm-start cache, best effort
  // server sync (fire-and-forget; memory is the immediate answer, server is the durable store)
  void api.saveFertRx(set.fieldId, set).then(
    () => {
      invalidateRxCoverage();
    },
    (e) => {
      console.debug("[fert-rx] server save unavailable — kept local", e);
    },
  );
  bumpRecSets();
  return list;
}

function adopted(sent: SavedRecSet, saved: unknown): SavedRecSet {
  if (!saved || typeof saved !== "object") return sent;
  const doc = saved as Partial<SavedRecSet>;
  if (doc.id !== sent.id || !Array.isArray(doc.nutrients)) return sent;
  // The response may carry the server freeze. The field id stays the one we sent.
  return { ...sent, ...doc, id: sent.id, fieldId: sent.fieldId };
}

// Batch runs wait for the server. A rejected save is not a saved Rx.
export async function commitRecSet(set: SavedRecSet): Promise<SavedRecSet[]> {
  const response = await api.saveFertRx(set.fieldId, set);
  const stored = adopted(set, response?.saved);
  const list = [
    stored,
    ...loadRecSets(set.fieldId).filter((s) => s.id !== stored.id),
  ];
  memSets.set(set.fieldId, list);
  persistRecSets(set.fieldId, list);
  bumpRecSets();
  invalidateRxCoverage();
  return list;
}

export function deleteRecSet(fieldId: string, id: string): SavedRecSet[] {
  const list = loadRecSets(fieldId).filter((s) => s.id !== id);
  memSets.set(fieldId, list);
  persistRecSets(fieldId, list);
  void api.deleteFertRx(fieldId, id).then(
    () => {
      invalidateRxCoverage();
    },
    (e) => {
      console.debug("[fert-rx] server delete unavailable — kept local", e);
    },
  );
  bumpRecSets();
  return list;
}

// Reconcile from the server on load (server wins on id; local-only sets not yet synced are kept).
// Returns the merged list + mirrors it to localStorage. Server unavailable → the local list.
export async function hydrateFertRx(fieldId: string): Promise<SavedRecSet[]> {
  try {
    const server = (await api.fetchFertRx(fieldId, "summary")) as SavedRecSet[];
    const byId = new Map<string, SavedRecSet>();
    for (const s of loadRecSets(fieldId)) byId.set(s.id, s);
    for (const s of server)
      if (typeof s?.id === "string" && Array.isArray(s.nutrients))
        byId.set(s.id, s);
    // NO CAP HERE: this used to slice(0, MAX_SETS) — so a field with 12+ newer saves silently lost
    // its older sets client-side even though the server returned them. The cap
    // belongs to the localStorage mirror alone (persistRecSets applies it); memory and the return
    // value carry EVERYTHING the server holds.
    const merged = [...byId.values()].sort((a, b) =>
      (b.createdAt ?? "").localeCompare(a.createdAt ?? ""),
    );
    memSets.set(fieldId, merged); // the session answer — sync readers see it via loadRecSets
    // Mirror through the SHEDDING writer, not a bare setItem: a quota failure must not discard the
    // whole merge. The mirror may keep fewer; memory keeps all.
    persistRecSets(fieldId, merged);
    bumpRecSets(); // server-reconciled sets may change coverage → refresh the filter/pills
    return merged;
  } catch {
    return loadRecSets(fieldId); // server not live / offline → local
  }
}

export type RxServerCoverage = {
  field_id: string;
  nutrient: string;
  covers_through_year: number | null;
};

/*
 * Flags read the org coverage query. Sets saved in this tab are unioned in so
 * a nutrient clears before that refetch lands. They never replace the server
 * rows: a local save of one nutrient must not drop the others. `null` means
 * the query has not succeeded, and the caller shows no nutrient flags.
 * localStorage is not evidence — the mirror sheds under quota.
 */
export function flagCoverage(
  fieldId: string,
  rows: readonly RxServerCoverage[] | null,
):
  | { nutrients?: string[]; coversThroughYear?: number; createdAt?: string }[]
  | null {
  if (rows == null) return null;
  const server: { nutrients: string[]; coversThroughYear: number }[] = [];
  for (const row of rows) {
    if (!row || row.field_id !== fieldId) continue;
    if (typeof row.nutrient !== "string" || row.nutrient.length === 0) continue;
    const raw = row.covers_through_year;
    const year = typeof raw === "number" ? raw : Number(raw);
    if (raw == null || !Number.isFinite(year)) continue;
    server.push({ nutrients: [row.nutrient], coversThroughYear: year });
  }
  return [...server, ...(memSets.get(fieldId) ?? [])];
}

export function invalidateRxCoverage(): void {
  void queryClient.invalidateQueries({ queryKey: qk.rxCoverage });
}

// `sm_fert_cover:*` is not coverage. Drop whatever is already stored so the
// origin quota is not held by keys the flags ignore.
export function deleteStaleCoverageStamps(): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(COVER_PREFIX)) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

deleteStaleCoverageStamps();
